import { ChevronDown, ChevronUp, Maximize2, Minimize2 } from "lucide-react";
import React, { createContext, useContext, useRef, useState } from "react";
import { colors, suspiciousColor } from "@client/constants";
import { useFullscreen } from "@client/hooks/useFullscreen";
import { useTopics } from "@client/hooks/useTopics";
import { nickname } from "@client/utils/nickname";
import { boolAsBit, fix, number, unit, unundefined } from "@client/utils/values";

interface PartialWrapperProps {
  /**
   * Explicit seznam topicIds, které tento wrapper "vlastní". Zobrazí se v hlavičce,
   * tlačítky lze toggle visibility, předají se do `hidden[]` v contextu.
   *
   * Refactor 2026-05 (CHART-DATA-REFACTOR Fáze 4): nahrazuje původní křehký
   * `React.Children.forEach` recursive scan. Children jsou nyní svobodné (mohou
   * být React.memo wrapped, nemusí mít topicId/topicIds prop).
   */
  topicIds: string[];
  showInitially?: boolean;
  children: React.ReactNode;
  className?: string;
  /**
   * Override zobrazené hodnoty v hlavičce per topicId. Když je topic v této mapě, hlavička
   * ukáže tuto hodnotu (formátovanou dle metadat topicu) místo živé hodnoty z `byTopic` MQTT
   * streamu. Použití: device-detail grafy ukazují živou telemetrii místo 15s MQTT průměru.
   */
  valueOverrides?: Record<string, number | null>;
  /**
   * Vlastní tlačítka do hlavičky, vlevo od fullscreenu. Wrapper je obecný, takže
   * ovládání specifické pro obsah (přepínač rozložení kamer) si vkládá volající.
   * Použij `BTN_CLASS`, ať to sedí se zbytkem hlavičky.
   */
  actions?: React.ReactNode;
}

interface PartialWrapperContextType {
  hidden: boolean[];
  setHidden: React.Dispatch<React.SetStateAction<boolean[]>>;
  isFullscreen: boolean;
  /** True když je wrapper rozbalený (Graph2 → enabled). Viz Fáze 6 (visibility-aware fetching). */
  visible: boolean;
}

const PartialWrapperContext = createContext<PartialWrapperContextType | undefined>(undefined);

/**
 * Tlačítka hlavičky.
 *
 * **Terč zůstává 44×44 px, ale výšku řádku už neurčuje.** Dřív bylo tlačítko `h-11 w-11`,
 * takže hlavička měřila 44 px kvůli dvěma ikonám — přestože text v ní má 20. U grafu
 * dvoustavové hodnoty (72 px) tím na chrome padla víc než třetina widgetu a na dashboardu
 * plném wrapperů to dělalo stovky pixelů.
 *
 * Řeší to pseudoelement: viditelné tlačítko má 28 px, kolem něj je neviditelných 44×44,
 * které pořád chytají tapy. Zmenšit rovnou `h-11` nešlo — 44px terč je záměr, kvůli kterému
 * se kdysi opravovalo „na mobilu to tlačítko nefunguje".
 *
 * `touch-manipulation` potlačuje 300ms double-tap prodlevu na iOS, kvůli které tapy
 * vypadaly jako ignorované.
 */
export const BTN_CLASS =
  "relative inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground " +
  "hover:text-foreground hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring touch-manipulation " +
  "after:absolute after:left-1/2 after:top-1/2 after:h-11 after:w-11 after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']";

// eslint-disable-next-line react-refresh/only-export-components
export const usePartialWrapper = () => {
  const context = useContext(PartialWrapperContext);
  if (!context) {
    throw new Error("usePartialWrapper must be used within a PartialWrapper");
  }
  return context;
};

/**
 * Optional varianta — vrátí undefined mimo provider (pro komponenty, které mohou
 * běžet jak uvnitř, tak mimo wrapper).
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useOptionalPartialWrapper = (): PartialWrapperContextType | undefined => useContext(PartialWrapperContext);

const PartialWrapper: React.FC<PartialWrapperProps> = ({
  topicIds,
  showInitially = true,
  children,
  className = "",
  valueOverrides,
  actions,
}) => {
  const [showAll, setShowAll] = useState(showInitially);
  const containerRef = useRef<HTMLDivElement | null>(null);
  /**
   * Fullscreen řeší sdílený hook. Původní inline implementace se rozhodovala podle
   * `document.fullscreenElement`, který na iPhonu (žádný element fullscreen) zůstává
   * `null` — exit větev tak byla nedosažitelná a fullscreen se na mobilu NEDAL ZAVŘÍT.
   *
   * `lockLandscape: false` schválně: kamery jsou ve fullscreenu pod sebou, takže
   * portrét je správná orientace. Uzamykal ho původní kód.
   */
  const fs = useFullscreen(containerRef, { lockScroll: true, lockLandscape: false });
  const isFullscreen = fs.active;
  const { values, topics, suspicious } = useTopics(topicIds);
  // Hidden array má fixní length matching topicIds. Pokud parent změní počet topicIds,
  // musí komponent remountnout (např. `key={topicIds.join("|")}`). V praxi se topicIds
  // za běhu nemění (App.tsx je hardcoded).
  const [hidden, setHidden] = useState<boolean[]>(() => Array(topicIds.length).fill(false));

  const toggleVisibility = () => setShowAll(!showAll);
  const toggleTopicVisibility = (index: number) => {
    setHidden((prev) => {
      const newHidden = [...prev];
      newHidden[index] = !newHidden[index];
      return newHidden;
    });
  };

  return (
    <div
      ref={containerRef}
      className={
        isFullscreen
          ? // Fullscreen = od kraje ke kraji: žádný radius, margin ani padding karty.
            // Externí `className` se schválně neaplikuje — může nést `rounded-*`
            // nebo odsazení, které by rámeček vrátilo zpět.
            // `h-dvh` místo `h-screen`: na iOS je 100vh vyšší než viditelný viewport,
            // takže spodek obsahu končil pod adresním řádkem. Safe-area kvůli notchi,
            // z-40 aby wrapper nekolidoval s Radix portály (z-50) a spotlightem (z-60).
            "flex fixed inset-0 w-screen h-dvh z-40 flex-col rounded-none bg-card overscroll-contain px-2 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
          : // `py-1.5` místo `p-2`: svisle se šetří, vodorovně ne — text by jinak lepil na okraj.
            `rounded-lg bg-muted/30 px-2 py-1.5 m-1 ${className}`
      }
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <h2 className="text-sm font-medium text-muted-foreground">
          {topicIds.length > 0
            ? topicIds.map((topicId, i) => {
                // valueOverrides umožní zobrazit jinou (např. živou telemetrii) hodnotu než z
                // byTopic MQTT streamu; formátování (decimals/unit) zůstává z metadat topicu.
                // Override hodnota je vždy "čerstvá" → neoznačovat jako suspicious.
                const hasOverride = valueOverrides != null && topicId in valueOverrides;
                const displayValue = hasOverride ? valueOverrides[topicId] : values[topicId];
                const valueSuspicious = !hasOverride && suspicious[topicId];
                return (
                  <span key={i}>
                    <span
                      onClick={() => toggleTopicVisibility(i)}
                      className={`hover:cursor-pointer ${hidden[i] ? "line-through decoration-neutral-500" : ""}`}
                    >
                      {topics[topicId] ? nickname(topics[topicId]) : topicId}
                    </span>

                    {topics[topicId] && (
                      <>
                        {": "}
                        <span
                          className={`font-semibold ${valueSuspicious ? "line-through decoration-amber-600" : ""}`}
                          style={{
                            color: valueSuspicious ? suspiciousColor : colors[i],
                          }}
                        >
                          {/*
                            Ano/ne se musí formátovat zvlášť. `parseFloat("false")` je NaN,
                            takže hlavička u dvoustavové veličiny vždycky psala „---",
                            i když hodnota v pořádku dorazila.
                          */}
                          {topics[topicId]?.valueType === "BOOLEAN"
                            ? unundefined(boolAsBit(displayValue), "---")
                            : unit(
                                unundefined(fix(number(String(displayValue)), topics[topicId]?.decimals ?? 1), "---"),
                                topics[topicId]?.unit ?? ""
                              )}
                        </span>
                      </>
                    )}
                    {i < topicIds.length - 1 && ", "}
                  </span>
                );
              })
            : "No topics found"}
        </h2>
        {/* Terče 44×44 px. Původní nahé 16px ikony se na telefonu prakticky nedaly
            trefit — druhá polovina toho, proč „na mobilu to tlačítko nefunguje". */}
        <div className="flex shrink-0 items-center gap-1">
          {/* Slot pro tlačítka konkrétního obsahu (např. přepínač rozložení kamer).
              Wrapper je obecný, takže si je vkládá ten, kdo ho používá. */}
          {actions}
          <button
            type="button"
            onClick={fs.toggle}
            className={BTN_CLASS}
            aria-label={isFullscreen ? "Ukončit fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
          </button>
          {/* Ve fullscreenu se sbalovat nedá — sbalený obsah by nechal prázdnou
              černou obrazovku a jediné, co by šlo, by bylo fullscreen ukončit. */}
          {!isFullscreen && (
            <button
              type="button"
              className={BTN_CLASS}
              onClick={toggleVisibility}
              aria-label={showAll ? "Skrýt obsah" : "Zobrazit obsah"}
            >
              {showAll ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </button>
          )}
        </div>
      </div>
      {/* Ve fullscreenu se obsah renderuje vždy, i když byl wrapper předtím sbalený —
          chevron tam není, takže by ho jinak nešlo rozbalit a zbyla by černá plocha. */}
      {(showAll || isFullscreen) && (
        // `min-h-0` je nutné, aby dítě ve fullscreenu mohlo mít vlastní scroll
        // (bez něj flex item nikdy nezmenší svůj content box a scroll se nikde neobjeví).
        // `overflow-auto`, ne `hidden`: wrapper používají i grafy a obsah, který se
        // nevejde, má zůstat dosažitelný, ne se uříznout. Stackované kamery mají
        // vlastní scroll a přesnou výšku, takže tady žádnou druhou lištu nevyrobí.
        <div className={`${isFullscreen ? "flex min-h-0 grow overflow-auto" : ""}`}>
          {/* `visible` řídí i to, jestli děti (grafy) fetchují data — ve fullscreenu
              jsou vidět vždy, i když byl wrapper předtím sbalený. */}
          <PartialWrapperContext.Provider value={{ hidden, setHidden, isFullscreen, visible: showAll || isFullscreen }}>
            {children}
          </PartialWrapperContext.Provider>
        </div>
      )}
    </div>
  );
};

export default PartialWrapper;
